<template>
    <PageHeader :crumbs="[{ label: $t('portal.tickets.title') }]" />

    <div class="tw-p-2">
        <div class="tw-h-full tw-w-full tw-p-4 tw-bg-white tw-rounded tw-border tw-border-b-2 tw-min-h-[calc(100dvh-125px)]">
            <div class="tw-justify-between tw-flex tw-items-center tw-gap-2">
                <FormField v-model="search" input-dir="ltr" :placeholder="$t('portal.tickets.search')"
                    left-icon="clarity:search-line" type="text" class="tw-min-w-[100px]" hide-errors @keyup.enter="fetch(1)" />
                <div class="tw-w-[160px]">
                    <FormField v-model="status" type="select" :options="statusFilterOptions" hide-errors @update:modelValue="fetch(1)" />
                </div>
            </div>

            <div class="tw-mt-5">
                <DataTable :columns="columns" :loading="loading" :count="items.length">
                    <tr v-for="t in items" :key="t.id + '-' + t.updated_at" :class="[rowClass, 'tw-cursor-pointer']" @click="goto(t)">
                        <td :class="cellClass"><span class="tw-font-medium tw-text-gray-800">{{ t.subject }}</span></td>
                        <td :class="cellClass"><span class="tw-text-gray-500">{{ t.customer_name }}</span></td>
                        <td :class="cellClass"><TicketStatusBadge :status="t.status" /></td>
                        <td :class="cellClass"><span class="tw-text-gray-600">{{ t.messages_count ?? 0 }}</span></td>
                        <td :class="cellClass"><span class="tw-text-gray-500 tw-whitespace-nowrap">{{ fmt.datetime(t.last_reply_at) }}</span></td>
                        <td :class="cellClass">
                            <div class="tw-flex tw-justify-end">
                                <a-action-button icon="solar:eye-bold-duotone" intent="dark" @click="goto(t)" />
                            </div>
                        </td>
                    </tr>
                </DataTable>
            </div>

            <div class="tw-my-4"></div>

            <TablePagination v-if="items.length" :has-next="options.current_page < options.last_page"
                :has-previous="options.current_page > 1" @next="fetch(options.current_page + 1)"
                @previous="fetch(options.current_page - 1)" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { paginate } from '@/api/tickets';
import PageHeader from '@/components/PageHeader.vue';
import FormField from '@/components/form/FormField';
import DataTable from '@/components/table/DataTable.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import TicketStatusBadge from './TicketStatusBadge.vue';
import { useFormat } from '@/composables/useFormat';
import { rowClass, cellClass } from '@/composables/useTableStyles';

const router = useRouter();
const i18n = useI18n();
const fmt = useFormat();

const items = ref([]);
const options = ref({ current_page: 1, last_page: 1 });
const loading = ref(false);
const search = ref('');
const status = ref('');

const columns = computed(() => [
    { label: i18n.t('portal.tickets.subject') },
    { label: i18n.t('fields.customer_name'), classes: ['tw-w-[180px]'] },
    { label: i18n.t('fields.status'), classes: ['tw-w-[120px]'] },
    { label: i18n.t('portal.tickets.messages'), classes: ['tw-w-[100px]'] },
    { label: i18n.t('portal.tickets.last-reply'), classes: ['tw-w-[160px]'] },
    { label: i18n.t('fields.actions'), classes: ['tw-w-[80px]', 'tw-text-end'] },
]);

const statusFilterOptions = computed(() => [
    { value: '', label: i18n.t('portal.tickets.all-statuses') },
    { value: 'open', label: i18n.t('portal.tickets.status-open') },
    { value: 'answered', label: i18n.t('portal.tickets.status-answered') },
    { value: 'closed', label: i18n.t('portal.tickets.status-closed') },
]);

async function fetch(page = 1) {
    loading.value = true;
    try {
        const { data } = await paginate({ per_page: 10, page, search: search.value, status: status.value });
        const { data: rows, ...ops } = data.data;
        items.value = rows;
        options.value = ops;
    } catch (e) { /* interceptor */ } finally {
        loading.value = false;
    }
}

const goto = (t) => router.push({ name: 'admin.tickets.show', params: { id: t.id } });

fetch();
</script>
